if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.formId = this.form.getAttribute('id');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('[data-add-to-cart-label]') || this.submitButton.querySelector('span');
        this.submitButtonTotal = this.submitButton.querySelector('[data-add-to-cart-total]');
        this.currentVariantPrice = Number(this.dataset.variantPrice);
        this.moneyFormat = this.dataset.moneyFormat;
        this.currencyCode = this.dataset.currencyCode;
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.onProductInfoChange = (event) => {
          if (
            event.target.matches('[name="quantity"]') &&
            event.target.getAttribute('form') === this.formId
          ) {
            this.updateStandardTotal();
          }
        };
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.isLensForm = Boolean(this.productInfo?.querySelector('.lens-two-eyes-wrapper'));
        this.quantityInput = document.querySelector(
          `[name="quantity"][form="${CSS.escape(this.formId)}"]`
        );

        document.addEventListener('input', this.onProductInfoChange, true);
        document.addEventListener('change', this.onProductInfoChange, true);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.dataset.sectionId) return;
          this.currentVariantPrice = Number(event.data.variant?.price);
          this.updateStandardTotal();
        });

        this.updateStandardTotal();
      }

      disconnectedCallback() {
        document.removeEventListener('input', this.onProductInfoChange, true);
        document.removeEventListener('change', this.onProductInfoChange, true);
        this.variantChangeUnsubscriber?.();
      }

      formatMoney(cents) {
        if (!Number.isFinite(cents) || !this.moneyFormat) return null;

        const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
          const value = (number / 100).toFixed(precision);
          const parts = value.split('.');
          parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
          return parts[0] + (parts[1] ? decimal + parts[1] : '');
        };

        const placeholder = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/)?.[1];
        const formats = {
          amount: [2, ',', '.'],
          amount_no_decimals: [0, ',', '.'],
          amount_with_comma_separator: [2, '.', ','],
          amount_no_decimals_with_comma_separator: [0, '.', ','],
          amount_with_space_separator: [2, ' ', ','],
          amount_no_decimals_with_space_separator: [0, ' ', ','],
          amount_with_apostrophe_separator: [2, "'", '.'],
        };
        const settings = formats[placeholder];
        if (!settings) return null;

        const localizedSettings = this.currencyCode === 'EUR' ? settings : [0, settings[1], settings[2]];
        return this.moneyFormat.replace(
          /\{\{\s*\w+\s*\}\}/,
          formatWithDelimiters(cents, ...localizedSettings)
        );
      }

      clearTotalPrice() {
        if (!this.submitButtonTotal) return;
        this.submitButtonTotal.textContent = '';
        this.submitButtonTotal.hidden = true;
      }

      setTotalPrice(cents) {
        const formatted = this.formatMoney(cents);
        if (!this.submitButtonTotal || !formatted || cents < 0) {
          this.clearTotalPrice();
          return;
        }

        this.submitButtonTotal.textContent = ` – ${formatted}`;
        this.submitButtonTotal.hidden = false;
      }

      updateStandardTotal() {
        if (this.isLensForm) {
          this.clearTotalPrice();
          return;
        }

        const quantityInput = this.quantityInput;
        const quantity = Number(quantityInput?.value || 1);
        const min = Number(quantityInput?.min || 1);
        const max = quantityInput?.max ? Number(quantityInput.max) : null;
        const step = Number(quantityInput?.step || 1);
        const validQuantity =
          Number.isInteger(quantity) &&
          quantity >= min &&
          (max === null || quantity <= max) &&
          (quantity - min) % step === 0;

        if (
          this.submitButton.disabled ||
          !validQuantity ||
          !Number.isFinite(this.currentVariantPrice)
        ) {
          this.clearTotalPrice();
          return;
        }

        this.setTotalPrice(this.currentVariantPrice * quantity);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          this.clearTotalPrice();
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          this.clearTotalPrice();
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          this.updateStandardTotal();
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
